import circleIconBlack from '@Resources/assets/symbology/circle-black.png';
import circleIconGreenDot from '@Resources/assets/symbology/circle-dot-green.png';
import circleIconGreen from '@Resources/assets/symbology/circle-green.png';
import circleIconOrange from '@Resources/assets/symbology/circle-orange.png';
import circleIconRed from '@Resources/assets/symbology/circle-red.png';
import squareIconGreenDot from '@Resources/assets/symbology/square-dot-green.png';
import squareIconGreen from '@Resources/assets/symbology/square-green.png';
import squareIconOrange from '@Resources/assets/symbology/square-orange.png';
import squareIconRed from '@Resources/assets/symbology/square-red.png';
import triangleIconGreen from '@Resources/assets/symbology/test/triangle-green.png';
import triangleIconRed from '@Resources/assets/symbology/test/triangle-red.png';
import { hexToRgb } from '@Utils/helpers/symbologyHelpers';
import { CircularProgress } from '@mui/material';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    GoogleMapComponent,
    IStreetViewPosition,
    StreetViewComponent,
} from '@Components/MapComponents/Google';
import GeoserverApi from '@Data/geoserver/Api';
import { QualityControlApi } from '@Data/qualityControl/Api';
import {
    analysisEntitiesState,
    assessedAnalysisEntitiesState,
    backPoleState,
    backSpanDataState,
    forePoleState,
    foreSpanDataState,
    qualityControlGeoserverFeaturesState,
    selectedAnalysisEntityState,
} from '@Data/qualityControl/Reducer';
import QualityControlStyles from '@Views/QualityControl/InProgressView.scss';
import Symbology from '@Views/QualityControl/styles/symbology.scss';
import Styles from './MapAndStreetView.scss';

export const QualityControlMapAndStreetView = () => {
    const selectedAnalysisEntity = useSelector(selectedAnalysisEntityState);
    const { folderIds, projectId } = useParams();
    const selectedProjectId = parseInt(projectId);

    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const poles = useSelector(qualityControlGeoserverFeaturesState);
    const [spans, setSpans] = useState<any>(null);
    const [initialCoordinates, setInitialCoordinates] = useState<any>(null);
    const analysisEntities = useSelector(analysisEntitiesState);
    const selectedBackPole = useSelector(backPoleState);
    const selectedForePole = useSelector(forePoleState);
    const qcedPoles = useSelector(assessedAnalysisEntitiesState);
    const backSpan = useSelector(backSpanDataState);
    const foreSpan = useSelector(foreSpanDataState);

    const resetState = () => {
        setHasLoaded(false);
        setSpans(null);
        setInitialCoordinates(null);
    };

    useEffect(() => {
        const getSpans = async () => {
            const spansResponse = await GeoserverApi.getSpansWfsData(
                folderIds,
                selectedProjectId
            );

            const filteredSpans =
                spansResponse.data && spansResponse.data.features
                    ? spansResponse.data.features.filter(
                          (span) => !_isNil(span.geometry)
                      )
                    : [];
            setSpans(filteredSpans);
        };

        const initialise = async () => {
            resetState();
            await getSpans();

            setHasLoaded(true);
        };

        if (!hasLoaded && !_isNil(folderIds) && !_isNil(selectedProjectId)) {
            initialise();
        }
    }, [selectedProjectId, folderIds, hasLoaded]);

    useEffect(() => {
        if (
            hasLoaded &&
            poles &&
            selectedAnalysisEntity &&
            !initialCoordinates
        ) {
            const initialPole = getSelectedPole();
            setInitialCoordinates(initialPole.geometry.coordinates);
        }
    }, [selectedAnalysisEntity, poles, hasLoaded, initialCoordinates]);

    const getSelectedPole = () => {
        return poles.find(
            (p) =>
                p.properties.entity_version_id ===
                selectedAnalysisEntity.entity_version_id
        );
    };

    const mapRef = useRef<GoogleMapComponent>(null);
    const streetViewRef = useRef<StreetViewComponent>(null);

    const [streetViewPosition, setStreetViewPosition] =
        useState<IStreetViewPosition>({ lat: 0, lng: 0 });
    const [streetViewHeading, setStreetViewHeading] = useState<number>(null);

    useEffect(() => {
        if (
            selectedAnalysisEntity &&
            poles &&
            mapRef.current &&
            streetViewRef.current
        ) {
            const selectedPole = getSelectedPole();
            // center on map
            mapRef.current.setCenter(
                selectedPole.geometry.coordinates[0],
                selectedPole.geometry.coordinates[1]
            );

            // set street view
            streetViewRef.current.setLocation(
                selectedPole.geometry.coordinates[0],
                selectedPole.geometry.coordinates[1]
            );
        }
    }, [selectedAnalysisEntity, poles, mapRef, streetViewRef]);

    const handleFeatureClick = (feature: any) => {
        const entityVersion = analysisEntities.find(
            (qcEntity) =>
                qcEntity.entity_version_id ===
                feature.properties.entity_version_id
        );
        QualityControlApi.setSelectedAnalysisEntity(entityVersion);
    };

    const handleStreetViewReposition = (lat: number, lng: number) => {
        setStreetViewPosition({
            lat: lat,
            lng: lng,
        });
    };

    const handleStreetViewPovChanged = (heading: number) => {
        setStreetViewHeading(heading);
    };

    const handlePoleIconSymbology = (pole: any) => {
        const isPoleQced = qcedPoles.find(
            (p) => p.entity_version_id === pole.properties.entity_version_id
        );

        if (!_isNil(pole.properties?.accuracy_label)) {
            const accuracyLabel = pole.properties.accuracy_label.toLowerCase();
            if (accuracyLabel.indexOf('reviewed') > -1) {
                return {
                    url: isPoleQced ? squareIconGreenDot : circleIconGreenDot,
                    height: 20,
                    width: 20,
                };
            } else if (accuracyLabel.indexOf('fixed') > -1) {
                return {
                    url: isPoleQced ? squareIconGreen : circleIconGreen,
                    height: 24,
                    width: 24,
                };
            } else if (accuracyLabel.indexOf('float') > -1) {
                return {
                    url: isPoleQced ? squareIconOrange : circleIconOrange,
                    height: 24,
                    width: 24,
                };
            } else if (accuracyLabel.indexOf('low') > -1) {
                return {
                    url: isPoleQced ? squareIconRed : circleIconRed,
                    height: 24,
                    width: 24,
                };
            } else {
                console.log(`found new accuracy label: ${accuracyLabel}`);
                return {
                    url: circleIconBlack,
                    height: 24,
                    width: 24,
                };
            }
        }
    };

    const handleSpanSymbology = (
        spanFeature
    ): [number, number, number, number] => {
        let spanType = spanFeature.properties?.span_type;

        if (_isNil(spanType)) {
            return [255, 125, 0, 100];
        } else {
            spanType = spanType.toLowerCase();
            if (
                spanType.indexOf('back') > -1 ||
                spanType.indexOf('fore') > -1
            ) {
                return hexToRgb(Symbology.span, 100);
            } else if (
                spanType.indexOf('drop') > -1 ||
                spanType.indexOf('building') > -1 ||
                spanType.indexOf('other') > -1
            ) {
                return hexToRgb(Symbology.dropSpan, 100);
            } else {
                return hexToRgb(Symbology.unknownSpan, 100);
            }
        }
    };

    const midSpanPhotoLocations = useMemo(() => {
        const output = [];
        if (
            _isNil(poles) ||
            _isNil(selectedAnalysisEntity) ||
            poles.length === 0
        ) {
            return null;
        }

        if (!_isNil(backSpan)) {
            output.push({ lat: backSpan.lat, lng: backSpan.lng, type: 'back' });
        }

        if (!_isNil(foreSpan)) {
            output.push({ lat: foreSpan.lat, lng: foreSpan.lng, type: 'fore' });
        }

        return output;
    }, [backSpan, foreSpan, selectedAnalysisEntity, poles]);

    const handleMidSpanPhotoLocationIconSymbology = (feature: any) => {
        return {
            url: feature.type === 'fore' ? triangleIconGreen : triangleIconRed,
            height: 24,
            width: 24,
        };
    };

    const mapComponent =
        hasLoaded && initialCoordinates ? (
            <GoogleMapComponent
                ref={mapRef}
                onClick={handleFeatureClick}
                streetViewPosition={streetViewPosition}
                streetViewHeading={streetViewHeading}
                poles={poles}
                spans={spans}
                selectedPole={selectedAnalysisEntity}
                selectedBackPole={selectedBackPole}
                selectedForePole={selectedForePole}
                initialCoordinates={initialCoordinates}
                poleIconSymbology={handlePoleIconSymbology}
                spanSymbology={handleSpanSymbology}
                poleIconUpdateArray={qcedPoles}
                visibleMidSpanPhotoLocations={midSpanPhotoLocations}
                midSpanPhotoLocationIconSymbology={
                    handleMidSpanPhotoLocationIconSymbology
                }
            />
        ) : (
            <div className={Styles.loadingContainer}>
                <CircularProgress />
            </div>
        );

    const streetViewComponent =
        hasLoaded && initialCoordinates ? (
            <StreetViewComponent
                ref={streetViewRef}
                onReposition={handleStreetViewReposition}
                onPovChange={handleStreetViewPovChanged}
                initialCoordinates={initialCoordinates}
            />
        ) : (
            <div className={Styles.loadingContainer}>
                <CircularProgress />
            </div>
        );

    return (
        <div className={QualityControlStyles.mapAndStreetViewContainer}>
            <div className={QualityControlStyles.mapContainer}>
                {mapComponent}
            </div>
            <div className={QualityControlStyles.streetViewContainer}>
                {streetViewComponent}
            </div>
        </div>
    );
};
